/**
 * Churn Analysis Page
 *
 * Subscription churn analytics: MDExam Legacy from Chargebee, every Manifold
 * tenant from its own subscriptions. Shows survival curves, cohort retention
 * and plan breakdown; Manifold tenants add retention by acquisition channel
 * and signup month × channel cohorts under the shared attribution settings.
 */

import { useState, useEffect, useCallback, Fragment } from 'react'
import { format, subDays } from 'date-fns'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import { churnAPI } from '../services/api'
import ChannelSplitCard, { channelLabel } from '../components/ChannelSplitCard'
import AttributionSettings, { DEFAULT_ENGINE } from '../components/AttributionSettings'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'
import { DateRangePicker } from '../components/DateRangePicker'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import { odinPatientUrl } from '../lib/portalAuthEvents'
import RetentionModels from '../components/RetentionModels'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Users, UserMinus, UserCheck, Clock, TrendingDown, Info, ChevronDown, ChevronRight, ExternalLink
} from 'lucide-react'

function getDefaultDateRange() {
  const end = new Date()
  const start = subDays(end, 364) // ~1 year for churn analysis
  return { from: start, to: end }
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

function formatPct(value) {
  if (value === null || value === undefined) return '-'
  return `${(value * 100).toFixed(1)}%`
}

export default function ChurnPage() {
  const [dateRange, setDateRange] = useState(getDefaultDateRange)
  // Cycles is the default because it is the axis that compares brands: a
  // monthly plan faces three renewal decisions in ninety days and an annual
  // one faces none, so a day axis reads plan mix as retention. Days is kept
  // because it is the honest answer to "how long do people stay".
  const [axis, setAxis] = useState('cycles')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
        axis,
      }

      const response = await churnAPI.getReport(params)

      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[CHURN] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [dateRange, axis])

  // Read off the RESPONSE, not the toggle: the Chargebee source has no cycle
  // axis and answers in days whatever was asked, so labelling from local state
  // would caption a day curve as cycles for mdexam.
  const isCycles = data?.axis === 'cycles'
  const isManifold = data?.source === 'manifold'

  // Retention per acquisition channel, fetched separately from the main curve.
  // It exists only for Manifold tenants — a Chargebee clinic's event history
  // carries no customer identity to bridge to a channel — so a failure here is
  // a missing card, not a broken page.
  const [bySource, setBySource] = useState(null)
  const [bySourceLoading, setBySourceLoading] = useState(false)
  const [bySourceError, setBySourceError] = useState(null)
  const [engine, setEngine] = useState(DEFAULT_ENGINE)
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page

  const fetchBySource = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return
    try {
      setBySourceLoading(true)
      setBySourceError(null)
      const response = await churnAPI.getBySource({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
        axis,
        engine,
        lookbackDays,
      })
      setBySource(response.data)
    } catch (err) {
      setBySource(null)
      setBySourceError(
        err.response?.data?.error?.message || err.message || 'Could not load the channel split'
      )
    } finally {
      setBySourceLoading(false)
    }
  }, [dateRange, axis, engine, lookbackDays])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => { fetchBySource() }, [fetchBySource])

  const summary = data?.summary

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Churn Analysis</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Subscription retention and churn metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Chargebee-only caveat: a Manifold tenant's report is its own data. */}
      {data && !isManifold && <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Currently showing MDExam Legacy (Chargebee) data only. Other clinics will be added when Konnektive subscription events are available.
        </span>
      </div>}

      {/* Controls */}
      <Card>
        <CardContent className="py-4 px-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            {isManifold && (
            <div className="flex rounded-lg border border-input bg-background p-1">
              {[
                { value: 'cycles', label: 'By cycle' },
                { value: 'days', label: 'By day' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setAxis(opt.value)}
                  title={opt.value === 'cycles'
                    ? 'Cycle 2 means renewed once — comparable across monthly, quarterly and annual plans'
                    : 'Wall-clock tenure. Plan mix affects the shape: an annual plan has no renewal decision in 90 days'}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    axis === opt.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
            <span className="text-xs text-muted-foreground">
              Filters by subscription creation date
            </span>
          </div>
        </CardContent>
      </Card>

      <DataFreshnessWarning />

      {/* Error */}
      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={fetchData} className="mt-3">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Analyzing subscription churn...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {!loading && !error && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Total</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary.total_subscribers)}</p>
              {summary.dq_excluded > 0 && (
                <p className="text-xs text-muted-foreground mt-1" title="Patients disqualified before their first renewal never became subscribers, so they count as neither active nor churned. Disqualified after a renewal is still churn.">
                  excl. {formatNumber(summary.dq_excluded)} DQ&apos;d at start
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <UserCheck className="h-4 w-4 text-green-600" />
                <span className="text-xs text-muted-foreground">Active</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{formatNumber(summary.active_subscribers)}</p>
              {summary.at_risk_subscribers > 0 && (
                <p className="text-xs text-amber-600 mt-1" title="Active subscribers whose renewal failed and who are still in dunning — listed under At Risk below.">
                  {formatNumber(summary.at_risk_subscribers)} at risk · in dunning
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <UserMinus className="h-4 w-4 text-red-500" />
                <span className="text-xs text-muted-foreground">Churned</span>
              </div>
              <p className="text-2xl font-bold text-red-500">{formatNumber(summary.churned_subscribers)}</p>
              {summary.switched_subscriptions > 0 && (
                <p className="text-xs text-muted-foreground mt-1" title="Stopped subscriptions whose customer stayed: another subscription was running, or started within 14 days. Not churn.">
                  + {formatNumber(summary.switched_subscriptions)} switched plan
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Churn Rate</span>
              </div>
              <p className="text-2xl font-bold">{summary.churn_rate}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Avg Lifetime</span>
              </div>
              <p className="text-2xl font-bold">{summary.avg_lifetime_days}d</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Median Lifetime</span>
              </div>
              <p className="text-2xl font-bold">{summary.median_lifetime_days}d</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Survival Curve */}
      {!loading && !error && data?.survival_curve?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Retention Curve (Kaplan-Meier)</CardTitle>
            <CardDescription>
              Probability of a subscriber remaining active over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={data.survival_curve}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="day"
                  label={{
                    value: isCycles ? 'Billing Cycles Completed' : 'Days Since Signup',
                    position: 'insideBottomRight',
                    offset: -5,
                  }}
                />
                <YAxis
                  domain={[0, 1]}
                  tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                  label={{ value: '% Retained', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                  formatter={(value, name) => {
                    if (name === 'survival') return [`${(value * 100).toFixed(1)}%`, 'Retained']
                    if (name === 'at_risk') return [formatNumber(value), 'Still subscribed']
                    return [value, name]
                  }}
                  labelFormatter={(label) => (isCycles ? `Cycle ${label}` : `Day ${label}`)}
                />
                <Line
                  type="stepAfter"
                  dataKey="survival"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name="survival"
                  // The API sends null past the last observation, where the
                  // estimator has no risk set left. connectNulls would draw a
                  // line straight through that gap and invent the measurement
                  // the null exists to withhold.
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* The same subscribers read by several retention models (services/retentionModels). */}
      {!loading && !error && data?.retention_models?.models?.length > 0 && (
        <RetentionModels data={data.retention_models} />
      )}

      {/* Cohort Retention Table */}
      {!loading && !error && data?.cohort_retention?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Cohort Retention</CardTitle>
            <CardDescription>
              Survival rate by signup month at key milestones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium min-w-[100px]">Cohort</th>
                    <th className="text-right p-3 font-medium">Size</th>
                    <th className="text-right p-3 font-medium">M1</th>
                    <th className="text-right p-3 font-medium">M2</th>
                    <th className="text-right p-3 font-medium">M3</th>
                    <th className="text-right p-3 font-medium">M6</th>
                    <th className="text-right p-3 font-medium">M9</th>
                    <th className="text-right p-3 font-medium">M12</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cohort_retention.map((row) => (
                    <tr key={row.cohort_month} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium text-sm">{row.cohort_month}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.cohort_size)}</td>
                      <td className="p-3 text-right text-sm font-mono">
                        <RetentionCell value={row.m1} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono">
                        <RetentionCell value={row.m2} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono">
                        <RetentionCell value={row.m3} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono">
                        <RetentionCell value={row.m6} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono">
                        <RetentionCell value={row.m9} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono">
                        <RetentionCell value={row.m12} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active today, but the renewal failed and dunning has not let them go. */}
      {!loading && !error && data?.at_risk && (data.at_risk.count > 0 || data.at_risk.available === false) && (
        <AtRiskCard data={data.at_risk} />
      )}

      {/* Why they stopped: involuntary vs voluntary, then sub-reasons. */}
      {!loading && !error && data?.churn_reasons?.total > 0 && (
        <ChurnReasonsCard data={data.churn_reasons} />
      )}

      {/* Three churn weightings across plan lengths, side by side until one is chosen. */}
      {!loading && !error && data?.renewal_churn?.by_length?.length > 0 && (
        <PlanLengthChurnCard data={data.renewal_churn} />
      )}

      {/* Plan Breakdown */}
      {!loading && !error && data?.by_plan?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Churn by Plan Type</CardTitle>
            <CardDescription>
              Breakdown by initial subscription plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Plan</th>
                    <th className="text-right p-3 font-medium">Subscribers</th>
                    <th className="text-right p-3 font-medium">Churned</th>
                    <th className="text-right p-3 font-medium">Churn Rate</th>
                    <th className="text-right p-3 font-medium hidden sm:table-cell">Avg Lifetime</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_plan.map((row) => (
                    <tr key={row.plan} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium text-sm">{row.plan}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.count)}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.churned)}</td>
                      <td className="p-3 text-right text-sm font-mono">
                        <span className={row.churn_rate > 50 ? 'text-red-500' : row.churn_rate > 30 ? 'text-amber-500' : 'text-green-600'}>
                          {row.churn_rate}%
                        </span>
                      </td>
                      <td className="p-3 text-right text-sm font-mono hidden sm:table-cell">{row.avg_lifetime_days}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Retention per acquisition channel.
          The curve above says how long customers stay; this says whether that
          differs by where they came from, which is the version that changes
          where money gets spent. A channel with the same LTV and a cliff at
          cycle 2 is a different decision from one with a flat curve.

          Cohorts get thin fast when a subscriber base is split four or five
          ways, so the backend flags any channel under its floor and the card
          renders it muted rather than hiding it — a channel with four
          subscriptions and three cancellations is still telling you something,
          it just is not a rate. */}
      {(bySource || bySourceLoading || bySourceError) && (
        <ChannelSplitCard
          action={<AttributionSettings models={bySource?.attribution?.models} engine={engine} onEngineChange={setEngine} />}
          title={`Retention by Acquisition Channel${bySource ? ` (${bySource.axis === 'cycles' ? 'billing cycles' : 'days'})` : ''}`}
          loading={bySourceLoading}
          error={bySourceError}
          coverage={bySource?.coverage}
          coverageNote={
            bySource
              ? `Churn rate is cancelled or lapsed subscriptions as a share of that channel's total. Channels under ${bySource.minCohort} subscriptions are marked thin — the rate is real but the cohort is too small to read a trend from.`
              : undefined
          }
          valueLabel="Churned"
          rows={(bySource?.byChannel || []).map(row => ({
            channel: row.channel,
            value: row.subscriptions > 0 ? (100 * row.churned) / row.subscriptions : 0,
            secondary: `${row.churned}/${row.subscriptions}`,
            sufficient: row.sufficient,
          }))}
          formatValue={pct => `${pct.toFixed(1)}%`}
          emptyMessage="No subscriptions to split"
        />
      )}

      {/* Signup month × channel. The cohort table above, split by where each
          cohort came from, so a channel's retention can be read over time
          rather than as one lifetime number. */}
      {(bySource?.cohorts || bySourceLoading) && (
        <ChannelCohortCard
          data={bySource}
          loading={bySourceLoading}
          settings={<AttributionSettings models={bySource?.attribution?.models} engine={engine} onEngineChange={setEngine} />}
        />
      )}

      {/* Empty state */}
      {!loading && !error && summary?.total_subscribers === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <p>No subscriptions found for this date range.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/** Retention cell with color coding */
function RetentionCell({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">-</span>
  }
  const pct = value * 100
  let colorClass = 'text-green-600'
  if (pct < 50) colorClass = 'text-red-500'
  else if (pct < 70) colorClass = 'text-amber-500'

  return <span className={colorClass}>{pct.toFixed(1)}%</span>
}

/**
 * Signup month × channel cohorts. Each month's row is the whole cohort; open
 * it for one row per channel, or pick a channel to read that channel's
 * months straight down. Channel-months under the backend's floor are muted.
 */
function ChannelCohortCard({ data, loading, settings }) {
  const [open, setOpen] = useState(() => new Set())
  const [channel, setChannel] = useState('all')
  const cohorts = data?.cohorts || []
  const points = data?.cohort_points || []
  const isCycles = data?.axis === 'cycles'
  const channels = [...new Map(cohorts.flatMap(c => c.byChannel).map(c => [c.channel, c.channel_label || channelLabel(c.channel)])).entries()]
  const toggle = month => setOpen(current => {
    const next = new Set(current)
    next.has(month) ? next.delete(month) : next.add(month)
    return next
  })
  const allOpen = cohorts.length > 0 && cohorts.every(c => open.has(c.cohort_month))
  const header = point => (isCycles ? `Cycle ${point}` : `Day ${point}`)
  const cells = row => points.map(({ key }) => (
    <td key={key} className="p-3 text-right text-sm font-mono"><RetentionCell value={row[key]} /></td>
  ))

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle className="text-lg">Cohorts by Acquisition Channel</CardTitle>
            <CardDescription>
              Retention by signup month{channel === 'all' ? ', split by channel' : ` for ${channels.find(([id]) => id === channel)?.[1] || channelLabel(channel)}`} · subscriptions started in the selected dates
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {settings}
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">Channel
              <select className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground" value={channel} onChange={e => setChannel(e.target.value)}>
                <option value="all">All channels</option>
                {channels.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </label>
            {channel === 'all' && (
              <Button variant="outline" size="sm" disabled={!cohorts.length} onClick={() => setOpen(allOpen ? new Set() : new Set(cohorts.map(c => c.cohort_month)))}>
                {allOpen ? 'Collapse all' : 'Expand all'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && !cohorts.length ? (
          <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Splitting cohorts by channel…</div>
        ) : !cohorts.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No subscriptions started in the selected dates.</p>
        ) : (
          <div className={`border rounded-lg overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium min-w-[160px]">{channel === 'all' ? 'Cohort › channel' : 'Cohort'}</th>
                  <th className="text-right p-3 font-medium">Size</th>
                  {points.map(({ key, point }) => <th key={key} className="text-right p-3 font-medium whitespace-nowrap">{header(point)}</th>)}
                </tr>
              </thead>
              <tbody>
                {channel === 'all' ? cohorts.map(month => (
                  <Fragment key={month.cohort_month}>
                    <tr className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => toggle(month.cohort_month)} aria-expanded={open.has(month.cohort_month)}>
                      <td className="p-3 font-medium text-sm">
                        <span className="inline-flex items-center gap-1">
                          {open.has(month.cohort_month) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {month.cohort_month}
                        </span>
                      </td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(month.cohort_size)}</td>
                      {cells(month)}
                    </tr>
                    {open.has(month.cohort_month) && month.byChannel.map(row => (
                      <tr key={row.channel} className={`border-t bg-muted/10 ${row.sufficient ? '' : 'opacity-50'}`} title={row.sufficient ? undefined : `Under ${data.minCohort} subscriptions: too few to read a trend`}>
                        <td className="p-3 pl-9 text-sm">{row.channel_label || channelLabel(row.channel)}</td>
                        <td className="p-3 text-right text-sm font-mono">{formatNumber(row.cohort_size)}</td>
                        {cells(row)}
                      </tr>
                    ))}
                  </Fragment>
                )) : cohorts.map(month => {
                  const row = month.byChannel.find(c => c.channel === channel)
                  if (!row) return null
                  return (
                    <tr key={month.cohort_month} className={`border-t hover:bg-muted/30 ${row.sufficient ? '' : 'opacity-50'}`} title={row.sufficient ? undefined : `Under ${data.minCohort} subscriptions: too few to read a trend`}>
                      <td className="p-3 font-medium text-sm">{month.cohort_month}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.cohort_size)}</td>
                      {cells(row)}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          {isCycles ? 'Cycle 2 is renewed once, on each subscription\'s own billing period (a 4-month bundle\'s cycle is 4 months).' : 'Days since signup.'} A dash means the cohort is too young to have reached that point. Paused and dunning subscriptions count as churned at the end of their last paid term. Muted rows are under {data?.minCohort ?? 10} subscriptions.
        </p>
      </CardContent>
    </Card>
  )
}

const pct = value => (value == null ? '—' : `${(value * 100).toFixed(1)}%`)

/**
 * Churn by plan length, three ways (backend services/churnWeighting): per
 * renewal decision, monthly-equivalent, and share of monthly run rate lost.
 */
function PlanLengthChurnCard({ data }) {
  const { by_length: rows, blended } = data
  const cell = 'p-3 text-right text-sm font-mono'
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Churn by Plan Length</CardTitle>
        <CardDescription>
          Per renewal decision: each time a term ran out, did the customer renew? Three ways to compare a monthly plan with a 4-, 7- or 13-month bundle.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Plan length</th>
                <th className="text-right p-3 font-medium">Subscriptions</th>
                <th className="text-right p-3 font-medium" title="Term ends reached, plus cancellations (a mid-term cancel declines the next renewal)">Decisions</th>
                <th className="text-right p-3 font-medium">Churned</th>
                <th className="text-right p-3 font-medium" title="Churned decisions / decisions">Per renewal</th>
                <th className="text-right p-3 font-medium" title="1 − (1 − per renewal)^(1 / months): the monthly rate that compounds to it">Monthly-equiv.</th>
                <th className="text-right p-3 font-medium" title="Monthly-equivalent compounded over 12 months">≈ Annual</th>
                <th className="text-right p-3 font-medium" title="Monthly run rate of churned decisions / of all decisions">Run rate lost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.plan_length} className={`border-t hover:bg-muted/30 ${row.decisions < 10 ? 'opacity-50' : ''}`}>
                  <td className="p-3 font-medium text-sm">{row.plan_length}</td>
                  <td className={cell}>{formatNumber(row.subscriptions)}</td>
                  <td className={cell}>{formatNumber(row.decisions)}</td>
                  <td className={cell}>{formatNumber(row.churned)}</td>
                  <td className={cell}>{pct(row.per_renewal)}</td>
                  <td className={cell}>{pct(row.monthly_equivalent)}</td>
                  <td className={cell}>{pct(row.annual_equivalent)}</td>
                  <td className={cell}>{pct(row.run_rate)}</td>
                </tr>
              ))}
              <tr className="border-t bg-muted/30 font-medium">
                <td className="p-3 text-sm">Blended</td>
                <td className={cell}>{formatNumber(blended.subscriptions)}</td>
                <td className={cell}>{formatNumber(blended.decisions)}</td>
                <td className={cell}>{formatNumber(blended.churned)}</td>
                <td className={cell} title="Every decision counts the same, whatever the plan length">{pct(blended.per_renewal)}</td>
                <td className={cell} title="Each plan length's monthly-equivalent, weighted by its monthly run rate">{pct(blended.monthly_equivalent)}</td>
                <td className={cell}>{pct(blended.annual_equivalent)}</td>
                <td className={cell}>{pct(blended.run_rate)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          <li><strong className="text-foreground">Per renewal</strong> counts customers per decision; a monthly and a 13-month decision weigh the same, so plan mix moves it.</li>
          <li><strong className="text-foreground">Monthly-equivalent</strong> converts each length to the monthly rate that compounds to its per-renewal rate, so lengths compare directly; blended by monthly run rate.</li>
          <li><strong className="text-foreground">Run rate lost</strong> is revenue churn: each decision weighs its price per month, so a lost bundle counts its monthly value, not one head.</li>
          <li>Bundles that have not reached a term end yet have made no decision and are not counted. Switches and staff test coupons are excluded. Muted rows have under 10 decisions.</li>
        </ul>
      </CardContent>
    </Card>
  )
}

/**
 * Churned subscriptions by reason (backend services/churnReasons): involuntary
 * or voluntary, then the reason and, where one is recorded, a sub-reason.
 */
function ChurnReasonsCard({ data }) {
  const [open, setOpen] = useState({})
  const failed = (data.sources || []).filter(s => !s.ok)
  const cell = 'p-3 text-right text-sm font-mono'
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Churn by Reason</CardTitle>
        <CardDescription>
          Manifold records no cancellation reason, so each churned subscription is read from its refund, its invoices and the patient&apos;s notes around the stop. Switches are not churn and are not here.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {failed.length > 0 && (
          <p className="mb-3 text-xs text-amber-600">
            Not read: {failed.map(s => s.source).join(', ')} — those reasons fall through to the next one that applies.
          </p>
        )}
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Reason</th>
                <th className="text-right p-3 font-medium">Churned</th>
                <th className="text-right p-3 font-medium">Share</th>
                <th className="text-right p-3 font-medium" title="Median days from start to stop">Median tenure</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map(group => (
                <Fragment key={group.key}>
                  <tr className="border-t bg-muted/30 font-medium">
                    <td className="p-3 text-sm">{group.label}</td>
                    <td className={cell}>{formatNumber(group.count)}</td>
                    <td className={cell}>{pct(group.share)}</td>
                    <td className={cell} />
                  </tr>
                  {group.reasons.filter(r => r.count > 0).map(reason => {
                    const expandable = reason.sub_reasons.length > 0
                    const isOpen = open[reason.key]
                    return (
                      <Fragment key={reason.key}>
                        <tr
                          className={`border-t hover:bg-muted/30 ${expandable ? 'cursor-pointer' : ''}`}
                          onClick={() => expandable && setOpen(o => ({ ...o, [reason.key]: !o[reason.key] }))}
                        >
                          <td className="p-3 pl-6 text-sm">
                            <span className="inline-flex items-center gap-1">
                              {expandable
                                ? (isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)
                                : <span className="w-3" />}
                              {reason.label}
                            </span>
                          </td>
                          <td className={cell}>{formatNumber(reason.count)}</td>
                          <td className={cell}>{pct(reason.share)}</td>
                          <td className={cell}>{reason.median_days == null ? '—' : `${reason.median_days}d`}</td>
                        </tr>
                        {isOpen && reason.sub_reasons.map(sub => (
                          <tr key={sub.key} className="border-t text-muted-foreground">
                            <td className="p-2 pl-12 text-sm">{sub.label}</td>
                            <td className={cell}>{formatNumber(sub.count)}</td>
                            <td className={cell}>{pct(sub.share)}</td>
                            <td className={cell} />
                          </tr>
                        ))}
                      </Fragment>
                    )
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          <li><strong className="text-foreground">Involuntary:</strong> disqualified (DQ refund or DQ note), did not ship, or payment failed (dunning, or the last invoice failed with no refund). A DQ is read before a failed card.</li>
          <li><strong className="text-foreground">Voluntary:</strong> refunded before renewal (by the refund&apos;s reason), paused and never resumed, or cancelled — with the reason from a note within 14 days of the stop where there is one.</li>
          <li>{formatNumber(data.tagged || 0)} of {formatNumber(data.total)} were cancelled in Odin with a reason picked by staff; that reason is used before anything inferred. The rest were cancelled elsewhere, or before Odin asked for a reason.</li>
          <li>Shares are of all churned subscriptions in the date range. Click a reason to see its sub-reasons.</li>
        </ul>
      </CardContent>
    </Card>
  )
}

const formatCents = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((cents || 0) / 100)
const formatDay = ms => (ms ? format(new Date(ms), 'MMM d') : '—')

export function AtRiskCard({ data }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">At Risk: In Dunning</CardTitle>
        <CardDescription>
          Still counted as active, but their renewal failed. Retrying: Manifold is retrying the card (day 1, 3, 7 and 14 after the failure).
          Paused: the retries ran out; they can still pay the dunning link until their paid term ends, after which they count as churned.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.available === false ? (
          <p className="text-sm text-amber-600">At-risk subscribers could not be read: {data.error}</p>
        ) : (
          <>
            <p className="mb-3 text-sm text-muted-foreground">
              <strong className="text-foreground">{formatNumber(data.count)}</strong> at risk
              ({formatNumber(data.retrying)} retrying, {formatNumber(data.paused)} paused) ·{' '}
              <strong className="text-foreground">{formatCents(data.amount_cents)}</strong> in renewals
              {data.truncated && <> · showing the first {formatNumber(data.rows.length)}</>}
            </p>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Customer</th>
                    <th className="text-left p-3 font-medium">Plan</th>
                    <th className="text-right p-3 font-medium">Renewal</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-right p-3 font-medium" title="The next retry, or for a paused subscription the day its paid term ends">Decides by</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(row => (
                    <tr key={row.subscription_id} className="border-t hover:bg-muted/30">
                      <td className="p-3">
                        <div className="font-medium">{row.customer_name || row.customer_email || 'Unknown customer'}</div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {row.customer_name && row.customer_email && <span>{row.customer_email}</span>}
                          {row.patient_id && (
                            <a href={odinPatientUrl(row.patient_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                              <ExternalLink className="w-3 h-3" /> Odin
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="p-3">{row.plan || '—'}</td>
                      <td className="p-3 text-right font-mono">{formatCents(row.amount_cents)}</td>
                      <td className="p-3">
                        {row.state === 'retrying'
                          ? <span className="text-amber-600">Retry {row.retry} of {row.retries}{row.failed_at ? ` · failed ${formatDay(row.failed_at)}` : ''}</span>
                          : <span className="text-red-500">Retries exhausted · paused</span>}
                      </td>
                      <td className="p-3 text-right">{formatDay(row.state === 'retrying' ? row.next_retry_at : row.term_end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
